import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    App,
    Button,
    Card,
    Col,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import { NodeIndexOutlined, ReloadOutlined, SettingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/zh-cn";

import {
    getPipelineGovernance,
    updatePipelineItemTypePolicy,
    updatePipelineTransitionPolicy,
} from "../../../api/v3/mailHubApi";
import { useAuth } from "../../../hooks/useAuth";
import {
    formatMailHubError,
    readMailHubData,
    validatePipelineGovernancePayload,
} from "../lib/contracts";

dayjs.extend(relativeTime);
dayjs.locale("zh-cn");

const { Text, Title } = Typography;

const STATUS_OPTIONS = [
    { label: "active", value: "active" },
    { label: "draining", value: "draining" },
    { label: "paused", value: "paused" },
    { label: "disabled", value: "disabled" },
];

const STATUS_META = {
    active: { color: "success", label: "active" },
    draining: { color: "gold", label: "draining" },
    paused: { color: "warning", label: "paused" },
    disabled: { color: "default", label: "disabled" },
};

const GROUP_LABELS = {
    core_fact: { label: "核心事实路径", color: "blue" },
    optional_derivative: { label: "可选派生", color: "purple" },
    side_effect: { label: "副作用路径", color: "red" },
};

function statusTag(status) {
    const meta = STATUS_META[status];
    return <Tag color={meta.color}>{meta.label}</Tag>;
}

function formatTime(value) {
    if (!value) return "—";
    return `${dayjs(value).format("YYYY-MM-DD HH:mm:ss")} (${dayjs(value).fromNow()})`;
}

function shortText(value, length = 120) {
    if (!value) return "—";
    return value.length > length ? `${value.slice(0, length)}...` : value;
}

function conditionText(condition) {
    if (!condition) return "—";
    return JSON.stringify(condition);
}

const PolicyEditorModal = ({ open, target, saving, onCancel, onSubmit }) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (!open || !target) return;
        form.setFieldsValue({ status: target.status, reason: "" });
    }, [form, open, target]);

    return (
        <Modal
            title={target?.kind === "item_type" ? "编辑任务类型策略" : "编辑触发链策略"}
            open={open}
            onCancel={onCancel}
            onOk={() => form.submit()}
            confirmLoading={saving}
            okText="保存策略"
            cancelText="取消"
            destroyOnClose
        >
            {target ? (
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    <Alert
                        type="warning"
                        showIcon
                        message="开启可选任务可能产生外部调用、业务写入或积压。业务副作用仍会继续受 mailbox 处理模式和能力开关约束。"
                    />
                    <div>
                        <Text type="secondary">目标</Text>
                        <div style={{ fontWeight: 600 }}>{target.title}</div>
                        <Text code>{target.id}</Text>
                    </div>
                    <Form form={form} layout="vertical" onFinish={onSubmit}>
                        <Form.Item name="status" label="状态" rules={[{ required: true, message: "请选择状态" }]}>
                            <Select options={STATUS_OPTIONS} />
                        </Form.Item>
                        <Form.Item
                            name="reason"
                            label="变更原因"
                            rules={[{ required: true, whitespace: true, message: "必须填写变更原因" }]}
                        >
                            <Input.TextArea rows={4} maxLength={1000} showCount placeholder="说明为什么启用、暂停、排空或禁用该策略" />
                        </Form.Item>
                    </Form>
                </Space>
            ) : null}
        </Modal>
    );
};

const PipelineGovernancePage = () => {
    const auth = useAuth();
    const { message } = App.useApp();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [editor, setEditor] = useState({ open: false, target: null });

    const canManage = auth.hasAnyPermission(["mail_hub:pipeline_manage", "mail_hub:manage"]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getPipelineGovernance();
            setData(validatePipelineGovernancePayload(readMailHubData(response, "Pipeline 治理")));
            setLoadError(null);
        } catch (error) {
            const errorMessage = formatMailHubError(error, "加载 Pipeline 任务配置失败");
            setData(null);
            setLoadError(errorMessage);
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [message]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const openEditor = useCallback((target) => {
        setEditor({ open: true, target });
    }, []);

    const closeEditor = useCallback(() => {
        setEditor({ open: false, target: null });
    }, []);

    const handleSubmit = useCallback(async (payload) => {
        if (!editor.target) return;
        setSaving(true);
        try {
            if (editor.target.kind === "item_type") {
                await updatePipelineItemTypePolicy(editor.target.id, payload);
            } else {
                await updatePipelineTransitionPolicy(editor.target.id, payload);
            }
            message.success("Pipeline 策略已更新");
            closeEditor();
            await loadData();
        } catch (error) {
            message.error(error?.response?.data?.message || error?.response?.data?.error || "保存 Pipeline 策略失败");
        } finally {
            setSaving(false);
        }
    }, [closeEditor, editor.target, loadData, message]);

    const itemColumns = useMemo(() => [
        {
            title: "任务类型",
            key: "item_type",
            fixed: "left",
            width: 260,
            render: (_, record) => {
                const group = GROUP_LABELS[record.group];
                return (
                    <Space direction="vertical" size={2}>
                        <Text strong>{record.display_name}</Text>
                        <Text code>{record.item_type}</Text>
                        <Space size={4} wrap>
                            <Tag color={group.color}>{group.label}</Tag>
                            {record.capability ? <Tag color="red">{record.capability}</Tag> : null}
                        </Space>
                    </Space>
                );
            },
        },
        {
            title: "状态",
            key: "status",
            width: 180,
            render: (_, record) => (
                <Space direction="vertical" size={4}>
                    {statusTag(record.status)}
                    <Text type="secondary">默认 {record.default_status}</Text>
                    <Text type={record.allow_create ? "success" : "secondary"}>创建：{record.allow_create ? "允许" : "禁止"}</Text>
                    <Text type={record.allow_execute ? "success" : "secondary"}>执行：{record.allow_execute ? "允许" : "禁止"}</Text>
                </Space>
            ),
        },
        {
            title: "执行配置",
            key: "config",
            width: 220,
            render: (_, record) => (
                <Space direction="vertical" size={2}>
                    <Text>{record.execution_kind}{record.pool_profile ? ` / ${record.pool_profile}` : ""}</Text>
                    <Text type="secondary">并发 {record.max_concurrency} · 超时 {record.timeout_seconds}s</Text>
                    <Text type="secondary">背压阈值 {record.bp_threshold}</Text>
                </Space>
            ),
        },
        {
            title: "运行摘要",
            key: "runtime",
            width: 220,
            render: (_, record) => {
                const summary = record.runtime_summary;
                return (
                    <Space direction="vertical" size={2}>
                        <Text>pending {summary.pending_count} · in_progress {summary.in_progress_count}</Text>
                        <Text>dead {summary.dead_count}</Text>
                        <Text type="secondary">24h 失败率 {Math.round(summary.failure_rate_24h * 100)}%</Text>
                    </Space>
                );
            },
        },
        {
            title: "最近错误",
            key: "latest_error",
            width: 280,
            render: (_, record) => {
                const summary = record.runtime_summary;
                if (!summary.latest_error_summary) return <Text type="secondary">—</Text>;
                return (
                    <Tooltip title={summary.latest_error_summary}>
                        <Space direction="vertical" size={2}>
                            <Text type="danger">{shortText(summary.latest_error_summary)}</Text>
                            <Text type="secondary">{formatTime(summary.latest_error_at)}</Text>
                        </Space>
                    </Tooltip>
                );
            },
        },
        {
            title: "策略审计",
            key: "policy",
            width: 280,
            render: (_, record) => {
                const override = record.policy_override;
                if (!override) return <Text type="secondary">使用注册默认策略</Text>;
                return (
                    <Space direction="vertical" size={2}>
                        <Text>{override.reason || "—"}</Text>
                        <Text type="secondary">{override.updated_by} · {formatTime(override.updated_at)}</Text>
                    </Space>
                );
            },
        },
        {
            title: "操作",
            key: "actions",
            width: 120,
            fixed: "right",
            render: (_, record) => (
                <Button
                    icon={<SettingOutlined />}
                    disabled={!canManage}
                    onClick={() => openEditor({
                        kind: "item_type",
                        id: record.item_type,
                        title: record.display_name,
                        status: record.status,
                    })}
                >
                    策略
                </Button>
            ),
        },
    ], [canManage, openEditor]);

    const transitionColumns = useMemo(() => [
        {
            title: "触发链",
            key: "transition",
            fixed: "left",
            width: 280,
            render: (_, record) => (
                <Space direction="vertical" size={2}>
                    <Text strong>{record.display_name}</Text>
                    <Text code>{record.transition_id}</Text>
                    <Text type="secondary">{record.target_scope || "all"}</Text>
                </Space>
            ),
        },
        {
            title: "From / To",
            key: "edge",
            width: 300,
            render: (_, record) => (
                <Space direction="vertical" size={6}>
                    <Space size={4} wrap>{record.from_item_types.map((item) => <Tag key={item}>{item}</Tag>)}</Space>
                    <Text type="secondary">→</Text>
                    <Space size={4} wrap>{record.to_item_types.map((item) => <Tag key={item} color="blue">{item}</Tag>)}</Space>
                </Space>
            ),
        },
        {
            title: "触发条件",
            key: "condition",
            width: 260,
            render: (_, record) => (
                <Space direction="vertical" size={2}>
                    <Tag color="geekblue">{record.trigger_mode}</Tag>
                    <Tooltip title={conditionText(record.condition)}>
                        <Text>{shortText(conditionText(record.condition), 100)}</Text>
                    </Tooltip>
                </Space>
            ),
        },
        {
            title: "状态",
            key: "status",
            width: 170,
            render: (_, record) => (
                <Space direction="vertical" size={4}>
                    {statusTag(record.status)}
                    <Text type="secondary">默认 {record.default_status}</Text>
                    <Text type={record.allow_create ? "success" : "secondary"}>创建：{record.allow_create ? "允许" : "禁止"}</Text>
                </Space>
            ),
        },
        {
            title: "运行摘要",
            key: "runtime",
            width: 180,
            render: (_, record) => {
                const summary = record.runtime_summary;
                return (
                    <Space direction="vertical" size={2}>
                        <Text>open {summary.open_count}</Text>
                        <Text>24h 创建 {summary.created_count_24h}</Text>
                        <Text type="secondary">最近 {formatTime(summary.latest_created_at)}</Text>
                    </Space>
                );
            },
        },
        {
            title: "策略审计",
            key: "policy",
            width: 280,
            render: (_, record) => {
                const override = record.policy_override;
                if (!override) return <Text type="secondary">使用注册默认策略</Text>;
                return (
                    <Space direction="vertical" size={2}>
                        <Text>{override.reason || "—"}</Text>
                        <Text type="secondary">{override.updated_by} · {formatTime(override.updated_at)}</Text>
                    </Space>
                );
            },
        },
        {
            title: "操作",
            key: "actions",
            width: 120,
            fixed: "right",
            render: (_, record) => (
                <Button
                    icon={<SettingOutlined />}
                    disabled={!canManage}
                    onClick={() => openEditor({
                        kind: "transition",
                        id: record.transition_id,
                        title: record.display_name,
                        status: record.status,
                    })}
                >
                    策略
                </Button>
            ),
        },
    ], [canManage, openEditor]);

    const tabs = useMemo(() => [
        {
            key: "item_types",
            label: "任务类型",
            children: (
                <Table
                    rowKey="item_type"
                    loading={loading}
                    columns={itemColumns}
                    dataSource={data ? data.item_types : []}
                    scroll={{ x: 1640 }}
                    pagination={{ pageSize: 20, showSizeChanger: true }}
                />
            ),
        },
        {
            key: "transitions",
            label: "触发链",
            children: (
                <Table
                    rowKey="transition_id"
                    loading={loading}
                    columns={transitionColumns}
                    dataSource={data ? data.transitions : []}
                    scroll={{ x: 1590 }}
                    pagination={{ pageSize: 20, showSizeChanger: true }}
                />
            ),
        },
    ], [data, itemColumns, loading, transitionColumns]);

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0 }}>
                    <NodeIndexOutlined style={{ marginRight: 8 }} />
                    Pipeline 任务配置
                </Title>
                <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>刷新</Button>
            </div>

            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="本页面治理已注册任务类型与触发链是否生效，不新增 DAG 边，也不替代 Work Items 实例管理。"
                description="默认策略保持保守：email_store、email_classify 和 store→classify 为核心事实路径默认开启；OCR、附件转存、报文解析、BJA 等可选派生和副作用路径默认关闭。"
            />

            {loadError ? (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Pipeline 任务配置不可用"
                    description={loadError}
                />
            ) : null}

            {data ? (
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={12} sm={8} md={4}><Card size="small"><Statistic title="任务类型" value={data.summary.item_type_total} /></Card></Col>
                    <Col xs={12} sm={8} md={4}><Card size="small"><Statistic title="任务 active" value={data.summary.item_type_active} /></Card></Col>
                    <Col xs={12} sm={8} md={4}><Card size="small"><Statistic title="任务阻止中" value={data.summary.item_type_blocking} /></Card></Col>
                    <Col xs={12} sm={8} md={4}><Card size="small"><Statistic title="触发链" value={data.summary.transition_total} /></Card></Col>
                    <Col xs={12} sm={8} md={4}><Card size="small"><Statistic title="触发链 active" value={data.summary.transition_active} /></Card></Col>
                    <Col xs={12} sm={8} md={4}><Card size="small"><Statistic title="触发链阻止中" value={data.summary.transition_blocking} /></Card></Col>
                </Row>
            ) : null}

            {!canManage ? (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="当前账号只有查看权限，策略更新需要 mail_hub:pipeline_manage 或 mail_hub:manage。"
                />
            ) : null}

            {data ? (
                <Card>
                    <Tabs items={tabs} />
                </Card>
            ) : null}

            <PolicyEditorModal
                open={editor.open}
                target={editor.target}
                saving={saving}
                onCancel={closeEditor}
                onSubmit={handleSubmit}
            />
        </div>
    );
};

export default PipelineGovernancePage;
